import CreateCustomerModalMutation from '@/ui/components/createCustomerModalMutation/CreateCustomerModalMutation';
import { Button } from '@payaca/ambrosia';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import TypeToSearchField, {
  TypeToSearchRenderOptionProps,
} from '@payaca/components/typeToSearchField/TypeToSearchField';
import * as customerActions from '@payaca/store/customer/customerActions';
import {
  GetListedCustomersRequestData,
  ListedCustomer,
} from '@payaca/types/listedCustomerTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { clsx } from '@payaca/utilities/cvaMerge';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from '../../../api/state';
import { getCustomer } from '../../../utils/stateAccessors';
import SelectCustomerModal from '../selectCustomerModal/SelectCustomerModal';

interface Option {
  id: number;
  name: string;
  primaryContactEmailAddress?: string;
}

type Props = {
  customerId?: number | null;
  onSelectCustomer?: (customerId?: number) => void;
};
const AddCustomerControl = ({ customerId, onSelectCustomer }: Props) => {
  const dispatch = useDispatch();
  const [getListedCustomersRequestData, setGetListedCustomersRequestData] =
    useState<GetListedCustomersRequestData>({
      pageSize: 20,
      pageNumber: 1,
      searchTerm: '',
    });
  const [showCreateEditJobCustomerModal, setShowCreateEditJobCustomerModal] =
    useState(false);
  const [showSearchCustomerTableModal, setShowSearchCustomerTableModal] =
    useState(false);

  const [customerName, setCustomerName] = useState<string>('');

  useEffect(() => {
    requestGetListedCustomersPage();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'requestGetListedCustomersPage'
  }, [getListedCustomersRequestData]);

  const requestGetListedCustomersPage = useCallback(() => {
    dispatch(
      customerActions.requestGetListedCustomersPage(
        getListedCustomersRequestData
      )
    );
  }, [getListedCustomersRequestData, dispatch]);

  const listedCustomersPage = useSelector((state) => {
    return state.customer?.listedCustomersPage;
  });
  const isGettingListedCustomersPage = useSelector(
    (state) => state.customer.isGettingListedCustomersPage
  );
  const customer = useSelector((state) =>
    customerId ? getCustomer(state, customerId) : undefined
  );

  useEffect(() => {
    if (customerId && customer) {
      setCustomerName(customer.name);
    }
  }, [customerId, customer]);

  const handleOpenCreateCustomerModal = useCallback((customerName?: string) => {
    if (customerName) {
      setCustomerName(customerName);
    }
    setShowCreateEditJobCustomerModal(true);
  }, []);

  const handleOpenAdvancedSearchCustomerModal = () => {
    // TODO: Open customer search modal (with search term?)
    setShowSearchCustomerTableModal(true);
  };

  const onSearchTermChange = useCallback((searchTerm: string) => {
    setCustomerName(searchTerm);
    // clear a selected customer
    onSelectCustomer?.(undefined);

    setGetListedCustomersRequestData(
      (getListedCustomersRequestData: GetListedCustomersRequestData) => {
        return {
          ...getListedCustomersRequestData,
          pageNumber: 1,
          searchTerm: searchTerm,
        };
      }
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'onSelectCustomer'
  }, []);

  const initialCreateCustomerFormState = useMemo(() => {
    return {
      name: customerName,
    };
  }, [customerName]);

  const renderOption = (
    option: Option,
    props: TypeToSearchRenderOptionProps
  ) => {
    return (
      <li
        {...props}
        className={clsx(
          'listed-job-customer-option flex space-x-3.5 items-center',
          props.className
        )}
      >
        {option.id === -1 && (
          <Button aria-label="Add customer" variant="secondary" size="xs">
            <UntitledIcon name="plus.3" className="h-3.5 w-3.5" />
          </Button>
        )}
        {option.id === -2 && <UntitledIcon name="search-lg.3" />}
        <div className="listed-job-customer-name-email-wrapper">
          <span>{option.name}</span>
          {option.primaryContactEmailAddress && (
            <span className="listed-job-customer-email">
              - {option.primaryContactEmailAddress}
            </span>
          )}
        </div>
      </li>
    );
  };

  let customerResults: Array<{
    id: ListedCustomer['customerId'];
    name: ListedCustomer['customerName'];
    primaryContactEmailAddress?: ListedCustomer['primaryContactEmailAddress'];
  }> = [];
  // only start showing customer results when started typing
  if (getListedCustomersRequestData.searchTerm) {
    customerResults = (listedCustomersPage?.items || []).map(
      (c: ListedCustomer) => ({
        id: c.customerId,
        name: c.customerName,
        primaryContactEmailAddress: c.primaryContactEmailAddress,
      })
    );
  }

  return (
    <>
      <TypeToSearchField
        value={customerName}
        styleVariant={InputStyleVariant.OUTSIZE}
        options={[
          ...customerResults,
          {
            id: -1,
            name: 'Create a new customer',
          },
          {
            id: -2,
            name: 'Advanced search',
          },
        ]}
        renderOption={renderOption}
        disableFilteringInComponent={true}
        isLoadingOptions={isGettingListedCustomersPage}
        onSelectOption={(option?: Option) => {
          if (option?.id === -1) {
            handleOpenCreateCustomerModal(
              getListedCustomersRequestData.searchTerm
            );
          } else if (option?.id === -2) {
            handleOpenAdvancedSearchCustomerModal();
          } else if (option?.id) {
            // selected existing customer
            if (option?.name) {
              setCustomerName(option.name);
            }
            onSelectCustomer?.(option.id);
          }
        }}
        placeholder="Type here to search customers"
        onSearchTermChange={onSearchTermChange}
      />
      {/* create customer modal */}
      <CreateCustomerModalMutation
        isOpen={showCreateEditJobCustomerModal && !showSearchCustomerTableModal}
        onClose={() => {
          setShowCreateEditJobCustomerModal(false);
        }}
        initialFormState={initialCreateCustomerFormState}
        mutationOptions={{
          onSuccess: (data) => {
            onSelectCustomer?.(+data.createCustomer.id);
            setShowCreateEditJobCustomerModal(false);
          },
        }}
      />
      <SelectCustomerModal
        isOpen={showSearchCustomerTableModal}
        onClose={() => setShowSearchCustomerTableModal(false)}
        onSelectCustomer={(customerId: number) => {
          setShowSearchCustomerTableModal(false);
          onSelectCustomer?.(customerId);
        }}
      />
    </>
  );
};

export default AddCustomerControl;
